// import node module libraries
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Form, Row, Table } from 'react-bootstrap';
import {
	useAsyncDebounce,
	useFilters,
	useGlobalFilter,
	usePagination,
	useTable
} from 'react-table';
import { FormSelect } from 'components/elements/form-select/FormSelect';

// import custom components
import GlobalFilter from 'components/elements/advance-table/GlobalFilter';
import Pagination from 'components/elements/advance-table/Pagination';

// import utility file
import SalledeclasseService from '../../../services/SalledeclasseService';

const ClassesListItems = () => {
	const [originalSalledeclasses, setOriginalSalledeclasses] = useState([]);
	const [salledeclasses, d] = useState([]);

	const SECTION_CHOICES = [
		{ value: 'en', label: 'Angolophone' },
		{ value: 'fr', label: 'Francophone' }
	];

	useEffect(() => {
		Promise.all([SalledeclasseService.getAllSalledeclasse()]).then(
			([salledeclassesData]) => {
				setOriginalSalledeclasses(salledeclassesData);
				d(salledeclassesData);
			}
		);
	}, []);

	const getFilterTerm = (event) => {
		let filterTerm = event.target.value;
		if (filterTerm !== '') {
			const newSalledeclassesList = originalSalledeclasses.filter(
				(salledeclasse) => {
					return salledeclasse.section === filterTerm;
				}
			);
			d(newSalledeclassesList);
		} else {
			d(originalSalledeclasses);
		}
	};

	const globalFilterFn = (rows, ids, filterValue) => {
		// Convert the filterValue to lowercase and remove any underscore characters
		const searchTerm = filterValue.toLowerCase().replace(/_/g, ' ');

		// Loop through each row in the table
		return rows.filter((row) => {
			// Use the some() method to check if there's at least one cell in the row
			// that matches the search term (ignoring case and underscores)
			return row.values.intitule_classe
				.toLowerCase()
				.replace(/_/g, ' ')
				.includes(searchTerm);
		});
	};

	const formatSalledeclasse = (value) => {
		return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c);
	};

	const columns = useMemo(
		() => [
			{ accessor: 'id', Header: 'ID', show: false },
			{
				accessor: 'intitule_classe',
				Header: 'Salle de classe',
				Cell: ({ value, row }) => {
					return (
						<div>
							{value ? (
								<Link to={`/pedagogie/notes/${row.original.id}/eleves`}>
									<h5 className="mb-0 ms-2 text-uppercase">
										{formatSalledeclasse(value)}
									</h5>
								</Link>
							) : null}
						</div>
					);
				}
			}
		],
		[]
	);

	const data = useMemo(() => {
		return salledeclasses.map((salledeclasse) => ({
			...salledeclasse
		}));
	}, [salledeclasses]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		nextPage,
		previousPage,
		state,
		gotoPage,
		pageCount,
		prepareRow,
		setGlobalFilter
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageSize: 10,
				hiddenColumns: columns.map((column) => {
					if (column.show === false) return column.accessor || column.id;
					else return false;
				})
			},
			globalFilter: globalFilterFn
		},
		useFilters,
		useGlobalFilter,
		usePagination
	);

	const { pageIndex, globalFilter } = state;

	return (
		<Fragment>
			<div className="overflow-hidden p-3">
				<Row className="justify-content-md-between mb-4 mb-xl-0 ">
					<Col xxl={3} lg={4} md={6} xs={12}>
						<GlobalFilter
							filter={globalFilter}
							setFilter={useAsyncDebounce(setGlobalFilter, 200)}
							placeholder="Rechercher classe"
						/>
					</Col>
					<Col xxl={3} lg={4} md={6} xs={12}>
						{/* records filtering options */}
						<Form.Control
							as={FormSelect}
							placeholder="Filtrer par section"
							options={SECTION_CHOICES}
							onChange={getFilterTerm}
						/>
					</Col>
				</Row>
			</div>

			<Table
				hover
				responsive
				{...getTableProps()}
				className="table text-nowrap table-centered"
			>
				<thead className="table-light">
					{headerGroups.map((headerGroup) => (
						<tr {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column) => (
								<th {...column.getHeaderProps()}>{column.render('Header')}</th>
							))}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{page.map((row) => {
						prepareRow(row);
						return (
							<tr {...row.getRowProps()}>
								{row.cells.map((cell) => {
									return (
										<td {...cell.getCellProps()}>{cell.render('Cell')}</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</Table>

			<Pagination
				previousPage={previousPage}
				pageCount={pageCount}
				pageIndex={pageIndex}
				gotoPage={gotoPage}
				nextPage={nextPage}
			/>
		</Fragment>
	);
};

export default ClassesListItems;
